<template>
  <div class="contador-laureano">
    <!-- Loading -->
    <div v-if="isLoading" class="loading-overlay">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
      <p>Carregando...</p>
    </div>

    <!-- Cronômetro Principal -->
    <div v-else-if="cronometroAtivo" class="cronometro-container">
      <div class="cronometro-header">
        <h1 class="cronometro-titulo">{{ cronometroAtivo.descricao }}</h1>
        <div class="cronometro-info">
          <span class="cronometro-duracao">
            Duração: {{ cronometroAtivo.tempoEmMinutos }} minutos
          </span>
        </div>
      </div>

      <div 
        class="cronometro-display"
        :style="{ backgroundColor: cronometroAtivo.cor }"
      >
        <div class="tempo-restante">
          <span class="minutos">{{ tempoRestante.minutos.toString().padStart(2, '0') }}</span>
          <span class="separador">:</span>
          <span class="segundos">{{ tempoRestante.segundos.toString().padStart(2, '0') }}</span>
        </div>
        
        <div class="progresso-container">
          <div class="progresso-barra">
            <div 
              class="progresso-fill"
              :style="{ width: progressoPercentual + '%' }"
            ></div>
          </div>
          <span class="progresso-texto">{{ progressoPercentual.toFixed(1) }}%</span>
        </div>
      </div>

      <div class="cronometro-status">
        <p v-if="statusCronometro === 'aguardando'" class="status-aguardando">
          Aguardando início: {{ formatarDataHora(cronometroAtivo.dataHoraInicio) }}
        </p>
        <p v-else-if="statusCronometro === 'executando'" class="status-executando">
          Em execução
        </p>
        <p v-else-if="statusCronometro === 'finalizado'" class="status-finalizado">
          Finalizado
        </p>
      </div>
    </div>

    <!-- Lista de Programação -->
    <div v-else class="programacao-container">
      <h2>Programação do Cronômetro</h2>
      <div v-if="empresa && empresa.cronometro && empresa.cronometro.length > 0">
        <div 
          v-for="(item, index) in empresa.cronometro" 
          :key="index"
          class="item-programacao"
          :class="{ 'item-ativo': index === cronometroAtualIndex }"
        >
          <div class="item-header">
            <div 
              class="item-cor"
              :style="{ backgroundColor: item.cor }"
            ></div>
            <h3>{{ item.descricao }}</h3>
          </div>
          <div class="item-detalhes">
            <p><strong>Duração:</strong> {{ item.tempoEmMinutos }} minutos</p>
            <p><strong>Início:</strong> {{ formatarDataHora(item.dataHoraInicio) }}</p>
          </div>
        </div>
      </div>
      <div v-else class="sem-programacao">
        <p>Nenhuma programação de cronômetro encontrada.</p>
      </div>
    </div>

    <!-- Snackbar para notificações -->
    <Snackbar ref="chamaSnackbar" />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { baseApiUrl } from "@/global";
import Snackbar from "../../componentes/snackbar.vue";

export default {
  name: "contadorLaureano",
  components: {
    Snackbar,
  },
  data() {
    return {
      isLoading: true,
      empresa: null,
      empresaId: "6888cd034aef6a4acfe457c7",
      cronometroAtivo: null,
      cronometroAtualIndex: -1,
      statusCronometro: "aguardando", // aguardando, executando, finalizado
      tempoRestante: {
        minutos: 0,
        segundos: 0,
      },
      progressoPercentual: 0,
      intervaloCronometro: null,
      intervaloVerificacao: null,
      intervaloRecarregamento: null,
      intervaloAtualizacaoSemCronometro: null,
    };
  },
  async mounted() {
    await this.carregarDadosEmpresa();
    this.iniciarVerificacaoProgramacao();
  },
  beforeDestroy() {
    this.limparTodosIntervalos();
  },
  methods: {
    async carregarDadosEmpresa() {
      try {
        this.isLoading = true;
        
        const response = await axios.post(
          baseApiUrl + "eventos/eventosFuturos",
          {
            empresaId: "6888cd034aef6a4acfe457c7",
          }
        );
        
        // Debug temporário - remover depois
        // eslint-disable-next-line no-console
        console.log("Resposta da API:", response.data);
        
        if (response.data) {
          this.empresa = response.data.empresa;
          
          // Debug temporário - remover depois
          // eslint-disable-next-line no-console
          console.log("Dados da empresa:", this.empresa);
          // eslint-disable-next-line no-console
          console.log("Eventos:", response.data.eventos);
          
          // Buscar cronômetro nos eventos
          if (response.data.eventos && response.data.eventos.length > 0) {
            // Pegar o primeiro evento que tenha cronômetro
            for (const evento of response.data.eventos) {
              if (evento.cronometro && Array.isArray(evento.cronometro) && evento.cronometro.length > 0) {
                this.empresa.cronometro = evento.cronometro;
                // eslint-disable-next-line no-console
                console.log("Cronômetro encontrado no evento:", evento.nome);
                // eslint-disable-next-line no-console
                console.log("Cronometro:", this.empresa.cronometro);
                break;
              }
            }
          }
          
          // Verificar se há programação de cronômetro
          if (this.empresa.cronometro && this.empresa.cronometro.length > 0) {
            this.verificarCronometroAtivo();
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar dados:", error);
        this.$refs.chamaSnackbar.snack("error", "Erro ao carregar dados da empresa");
      } finally {
        this.isLoading = false;
      }
    },

    iniciarVerificacaoProgramacao() {
      // Verificar a cada 5 segundos se há um novo cronômetro para iniciar
      this.intervaloVerificacao = setInterval(() => {
        this.verificarCronometroAtivo();
      }, 5000);
      
      // Iniciar atualização automática quando não há cronômetro ativo
      this.iniciarAtualizacaoSemCronometro();
    },

    iniciarAtualizacaoSemCronometro() {
      // Limpar intervalo anterior se existir
      if (this.intervaloAtualizacaoSemCronometro) {
        clearInterval(this.intervaloAtualizacaoSemCronometro);
      }
      
      // eslint-disable-next-line no-console
      console.log("Iniciando atualização automática a cada 60 segundos quando não há cronômetro ativo");
      
      // Recarregar dados a cada minuto quando não há cronômetro ativo
      this.intervaloAtualizacaoSemCronometro = setInterval(() => {
        // eslint-disable-next-line no-console
        console.log("Verificando atualização automática. Cronômetro ativo:", !!this.cronometroAtivo);
        if (!this.cronometroAtivo) {
          // eslint-disable-next-line no-console
          console.log("Atualizando dados automaticamente...");
          this.carregarDadosEmpresa();
        }
      }, 60000);
    },

    pararAtualizacaoSemCronometro() {
      if (this.intervaloAtualizacaoSemCronometro) {
        clearInterval(this.intervaloAtualizacaoSemCronometro);
        this.intervaloAtualizacaoSemCronometro = null;
      }
    },

    verificarCronometroAtivo() {
      if (!this.empresa || !this.empresa.cronometro || !Array.isArray(this.empresa.cronometro)) {
        // eslint-disable-next-line no-console
        console.log("Sem dados de cronômetro válidos");
        return;
      }

      const agora = moment();
      let cronometroEncontrado = false;

      // eslint-disable-next-line no-console
      console.log("Verificando cronômetros. Hora atual:", agora.format("DD/MM/YYYY HH:mm:ss"));

      for (let i = 0; i < this.empresa.cronometro.length; i++) {
        const item = this.empresa.cronometro[i];
        const inicioItem = moment(item.dataHoraInicio);
        const fimItem = moment(inicioItem).add(item.tempoEmMinutos, "minutes");

        // eslint-disable-next-line no-console
        console.log(`Cronômetro ${i}:`, {
          descricao: item.descricao,
          inicio: inicioItem.format("DD/MM/YYYY HH:mm:ss"),
          fim: fimItem.format("DD/MM/YYYY HH:mm:ss"),
          ativo: agora.isBetween(inicioItem, fimItem, null, "[]")
        });

        // Verificar se estamos no período de execução deste cronômetro
        if (agora.isBetween(inicioItem, fimItem, null, "[]")) {
          if (!this.cronometroAtivo || this.cronometroAtualIndex !== i) {
            this.iniciarCronometro(item, i);
          }
          cronometroEncontrado = true;
          break;
        }
      }

      // Se não há cronômetro ativo, parar o atual se existir
      if (!cronometroEncontrado && this.cronometroAtivo) {
        this.pararCronometro();
      }
    },

    iniciarCronometro(cronometro, index) {
      // Parar cronômetro anterior se existir (mas manter outros intervalos)
      this.limparIntervalos();
      
      this.cronometroAtivo = cronometro;
      this.cronometroAtualIndex = index;
      this.statusCronometro = "executando";
      
      // eslint-disable-next-line no-console
      console.log("Cronômetro iniciado:", cronometro.descricao);
      
      // Calcular tempo restante inicial
      this.atualizarTempoRestante();
      
      // Iniciar interval para atualizar o cronômetro
      this.intervaloCronometro = setInterval(() => {
        this.atualizarTempoRestante();
      }, 1000);
    },

    atualizarTempoRestante() {
      if (!this.cronometroAtivo) return;

      const agora = moment();
      const inicio = moment(this.cronometroAtivo.dataHoraInicio);
      const fim = moment(inicio).add(this.cronometroAtivo.tempoEmMinutos, "minutes");
      
      // Calcular tempo restante
      const tempoRestanteMs = fim.diff(agora);
      
      if (tempoRestanteMs <= 0) {
        // Cronômetro finalizado
        this.finalizarCronometro();
        return;
      }
      
      const duration = moment.duration(tempoRestanteMs);
      this.tempoRestante.minutos = Math.floor(duration.asMinutes());
      this.tempoRestante.segundos = duration.seconds();
      
      // Calcular progresso
      const tempoTotalMs = fim.diff(inicio);
      const tempoDecorridoMs = agora.diff(inicio);
      this.progressoPercentual = Math.max(0, Math.min(100, (tempoDecorridoMs / tempoTotalMs) * 100));
    },

    finalizarCronometro() {
      this.statusCronometro = "finalizado";
      this.tempoRestante.minutos = 0;
      this.tempoRestante.segundos = 0;
      this.progressoPercentual = 100;
      
      // Limpar interval do cronômetro
      if (this.intervaloCronometro) {
        clearInterval(this.intervaloCronometro);
        this.intervaloCronometro = null;
      }
      
      // Recarregar dados quando o cronômetro zerar
      this.carregarDadosEmpresa();
      
      // Aguardar 3 segundos e então limpar o cronômetro ativo
      setTimeout(() => {
        this.pararCronometro();
      }, 3000);
    },

    pararCronometro() {
      // eslint-disable-next-line no-console
      console.log("Parando cronômetro - voltando para modo de espera");
      
      this.cronometroAtivo = null;
      this.cronometroAtualIndex = -1;
      this.statusCronometro = "aguardando";
      this.tempoRestante.minutos = 0;
      this.tempoRestante.segundos = 0;
      this.progressoPercentual = 0;
      
      // Recarregar dados quando cronômetro para
      this.carregarDadosEmpresa();
      
      // Limpar apenas intervalos do cronômetro ativo
      this.limparIntervalos();
      
      // eslint-disable-next-line no-console
      console.log("Cronômetro parado. Atualização automática deve continuar funcionando.");
    },

    limparIntervalos() {
      // Limpar apenas intervalos relacionados ao cronômetro ativo
      if (this.intervaloCronometro) {
        clearInterval(this.intervaloCronometro);
        this.intervaloCronometro = null;
      }
    },

    limparTodosIntervalos() {
      // Limpar todos os intervalos (usado no beforeDestroy)
      if (this.intervaloCronometro) {
        clearInterval(this.intervaloCronometro);
        this.intervaloCronometro = null;
      }
      if (this.intervaloVerificacao) {
        clearInterval(this.intervaloVerificacao);
        this.intervaloVerificacao = null;
      }
      if (this.intervaloRecarregamento) {
        clearInterval(this.intervaloRecarregamento);
        this.intervaloRecarregamento = null;
      }
      if (this.intervaloAtualizacaoSemCronometro) {
        clearInterval(this.intervaloAtualizacaoSemCronometro);
        this.intervaloAtualizacaoSemCronometro = null;
      }
    },

    formatarDataHora(dataHora) {
      return moment(dataHora).format("DD/MM/YYYY HH:mm:ss");
    },
  },
};
</script>

<style scoped>
.contador-laureano {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Roboto', sans-serif;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 20px;
}

.loading-overlay p {
  font-size: 18px;
  margin: 0;
}

/* Cronômetro Principal - Tela Cheia */
.cronometro-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
}

.cronometro-header {
  margin-bottom: 50px;
  color: white;
}

.cronometro-titulo {
  font-size: 5rem;
  font-weight: bold;
  color: white;
  margin: 0 0 30px 0;
  text-transform: uppercase;
  letter-spacing: 4px;
  text-shadow: 4px 4px 8px rgba(0, 0, 0, 0.7);
  line-height: 1.1;
}

.cronometro-info {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.3rem;
}

.cronometro-duracao {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1.1rem;
}

.cronometro-display {
  border-radius: 15px;
  padding: 40px 20px;
  margin: 30px 0;
  color: white;
  box-shadow: inset 0 4px 8px rgba(0, 0, 0, 0.2);
}

.tempo-restante {
  font-size: 12rem;
  font-weight: bold;
  margin-bottom: 30px;
  text-shadow: 6px 6px 12px rgba(0, 0, 0, 0.7);
  color: white;
  line-height: 1;
}

.minutos, .segundos {
  display: inline-block;
  min-width: 2ch;
}

.separador {
  margin: 0 10px;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
}

.progresso-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.progresso-barra {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.progresso-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  transition: width 1s ease;
  border-radius: 4px;
}

.progresso-texto {
  font-size: 1.1rem;
  font-weight: bold;
  min-width: 50px;
}

.cronometro-status {
  margin-top: 20px;
}

.status-aguardando {
  color: #ffeb3b;
  font-size: 2rem;
  font-weight: 600;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
}

.status-executando {
  color: #4caf50;
  font-size: 2rem;
  font-weight: 600;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
}

.status-finalizado {
  color: #f44336;
  font-size: 2rem;
  font-weight: 600;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
}

/* Lista de Programação */
.programacao-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
}

.programacao-container h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 2rem;
  font-weight: bold;
}

.item-programacao {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 15px;
  border-left: 5px solid #ddd;
  transition: all 0.3s ease;
}

.item-programacao:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.item-programacao.item-ativo {
  background: #e8f5e8;
  border-left-color: #4caf50;
  box-shadow: 0 5px 15px rgba(76, 175, 80, 0.2);
}

.item-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.item-cor {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.item-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
}

.item-detalhes {
  margin-left: 35px;
  color: #666;
}

.item-detalhes p {
  margin: 5px 0;
  font-size: 0.95rem;
}

.sem-programacao {
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  padding: 40px 20px;
}

/* Responsividade */
@media (max-width: 768px) {
  .contador-laureano {
    padding: 10px;
  }

  .cronometro-container,
  .programacao-container {
    padding: 20px;
  }

  .cronometro-titulo {
    font-size: 3rem;
  }

  .tempo-restante {
    font-size: 7rem;
  }

  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .item-detalhes {
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .cronometro-titulo {
    font-size: 2.5rem;
  }

  .tempo-restante {
    font-size: 5rem;
  }

  .progresso-container {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
